import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Card, Field } from '../../components/kit';
import { ResetPasswordModal } from '../../components/ResetPasswordModal';
import { PageHead, Toolbar, Search, ListTable, Pager, CustomerCell, ActBtn } from '../../components/list';
import { adminApi, type Staff as StaffUser } from '../../api/admin';
import { ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import '../page.css';
import './admin.css';

const EMPTY = { fullName: '', email: '', role: 'manager' as 'admin' | 'manager', password: '' };
type FormState = typeof EMPTY;
const ROLE_LABEL: Record<string, string> = { admin: 'Admin', manager: 'Manager' };

export function Staff() {
  const { user } = useAuth();
  const [rows, setRows] = useState<StaffUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<null | 'create' | number>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState<StaffUser | null>(null);

  const load = () => { adminApi.staff(q, page).then((r) => { setRows(r.staff); setTotal(r.total); }); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [page]);

  const openCreate = () => { setForm({ ...EMPTY }); setError(null); setMode('create'); };
  const openEdit = (u: StaffUser) => {
    setForm({ fullName: u.full_name ?? '', email: u.email, role: u.role, password: '' });
    setError(null);
    setMode(u.id);
  };

  const toggleActive = async (u: StaffUser) => {
    setError(null);
    try { await adminApi.updateStaff(u.id, { isActive: !u.is_active }); load(); }
    catch (err) { setError(err instanceof ApiRequestError ? err.message : 'İşlem başarısız'); }
  };

  const remove = async (u: StaffUser) => {
    if (!window.confirm(`"${u.full_name || u.email}" kullanıcısı kalıcı olarak silinecek. Emin misiniz?`)) return;
    setError(null);
    try { await adminApi.deleteStaff(u.id); load(); }
    catch (err) { setError(err instanceof ApiRequestError ? err.message : 'Silinemedi'); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'create') {
        await adminApi.createStaff(form);
      } else if (typeof mode === 'number') {
        await adminApi.updateStaff(mode, { fullName: form.fullName, email: form.email, role: form.role });
      }
      setMode(null);
      setForm({ ...EMPTY });
      load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const pages = Math.max(1, Math.ceil(total / 20));
  const isEdit = typeof mode === 'number';

  return (
    <div className="page">
      <PageHead
        eyebrow="Yönetim" title="Kullanıcı Yönetimi" sub="Admin ve Manager hesaplarını yönetin. Bayiler için “Bayi Yönetimi”ni kullanın."
        right={
          <button className="lt-btn primary" onClick={mode === 'create' ? () => setMode(null) : openCreate}>
            {mode === 'create' ? 'Vazgeç' : '+ Yeni Kullanıcı'}
          </button>
        }
      />

      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {mode !== null && (
        <Card title={isEdit ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'} className="bank-card">
          <form onSubmit={submit}>
            <div className="inline-form">
              <Field label="Ad Soyad" id="fn" value={form.fullName} onChange={set('fullName')} required />
              <Field label="E-posta" id="em" type="email" value={form.email} onChange={set('email')} required />
              <div className="field">
                <label htmlFor="role">Rol</label>
                <select id="role" className="input" value={form.role} onChange={set('role')}>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {!isEdit && <Field label="Geçici Şifre (min 8)" id="pw" type="text" value={form.password} onChange={set('password')} required />}
            </div>
            <div className="actions">
              <Button type="submit" disabled={busy}>{busy ? 'Kaydediliyor…' : isEdit ? 'Kaydet' : 'Oluştur'}</Button>
              <Button type="button" variant="secondary" onClick={() => setMode(null)}>Vazgeç</Button>
            </div>
          </form>
        </Card>
      )}

      <Toolbar>
        <Search value={q} onChange={setQ} onEnter={() => { setPage(1); load(); }} placeholder="Ad / e-posta ara…" />
        <span className="lt-grow" />
        <span className="lt-count">{total} kullanıcı</span>
      </Toolbar>

      <ListTable
        storageKey="admin-staff"
        head={[
          { label: 'Kullanıcı', width: 280 },
          { label: 'E-posta', width: 300 },
          { label: 'Rol', width: 120 },
          { label: 'Durum', width: 120 },
          { label: 'İşlem', width: 170, right: true },
        ]}
        empty={rows.length === 0 ? <div className="lt-empty">Kullanıcı bulunamadı.</div> : undefined}
        pager={<Pager page={page} pages={pages} total={total} unit="kullanıcı" onPage={setPage} />}
      >
        {rows.map((u) => (
          <tr key={u.id}>
            <td><CustomerCell primary={u.full_name || u.email} secondary={u.id === user?.id ? '(siz)' : undefined} /></td>
            <td title={u.email}>{u.email}</td>
            <td><span style={{ fontWeight: 600, fontSize: 12.5, color: u.role === 'admin' ? 'var(--accent-ink)' : 'var(--muted)' }}>{ROLE_LABEL[u.role]}</span></td>
            <td><span className={`lt-pill ${u.is_active ? 'ok' : 'fail'}`}>{u.is_active ? 'Aktif' : 'Pasif'}</span></td>
            <td>
              <div className="lt-acts">
                <ActBtn icon="edit" title="Düzenle" onClick={() => openEdit(u)} />
                <ActBtn icon="key" title="Şifre Sıfırla" onClick={() => setResetFor(u)} />
                {u.id !== user?.id && (
                  <ActBtn icon="power" title={u.is_active ? 'Pasifleştir' : 'Aktifleştir'} onClick={() => toggleActive(u)} />
                )}
                {u.id !== user?.id && (
                  <ActBtn icon="trash" tone="danger" title="Sil" onClick={() => remove(u)} />
                )}
              </div>
            </td>
          </tr>
        ))}
      </ListTable>

      <ResetPasswordModal
        target={resetFor ? { id: resetFor.id, label: resetFor.full_name || resetFor.email } : null}
        resetFn={adminApi.resetStaffPassword}
        onClose={() => setResetFor(null)}
      />
    </div>
  );
}
